import { create } from 'zustand'
import { api } from '../lib/api'
import type { LoadState } from '../hooks/useAsyncData'

export interface Framing {
  id: string
  accomplishment_id: string
  label: string
  text: string
  auto_generated: boolean
  approved: boolean
  needs_review: boolean
  created_at: string | null
}

export interface QaOrigin {
  question: string
  answer_snippet: string
  question_id?: string
}

export interface Accomplishment {
  id: string
  employer: string
  role_title: string
  period: string | null
  authority_notes: string | null
  role_context: string | null
  competency_tags: string[]
  context_tags: string[]
  extraction_status: string
  source: string
  created_at: string | null
  updated_at: string | null
  framings?: Framing[]
  qa_origin: QaOrigin | null
  display_facts: Record<string, unknown> | null
}

export interface VocabularyTerm {
  term: string
  definition: string
}

interface ApiResponse<T> {
  data: T
}

interface LibraryFilters {
  search: string
  employer: string
  competency: string
  // Default view shows actionable proofs (active + needs_review). When true,
  // archived proofs are included as well.
  includeArchived: boolean
}

interface LibraryState {
  accomplishments: Accomplishment[]
  selectedAccomplishment: Accomplishment | null
  filters: LibraryFilters
  competencyVocabulary: VocabularyTerm[]
  contextTagVocabulary: VocabularyTerm[]
  loadState: LoadState
  error: string | null
  framingsLoading: boolean

  fetchAccomplishments: () => Promise<void>
  fetchAccomplishment: (id: string) => Promise<void>
  setFilter: (key: 'search' | 'employer' | 'competency', value: string) => void
  setIncludeArchived: (value: boolean) => void
  clearFilters: () => void
  createAccomplishment: (data: AccomplishmentCreateData) => Promise<void>
  updateAccomplishment: (id: string, data: AccomplishmentUpdateData) => Promise<void>
  archiveAccomplishment: (id: string) => Promise<void>
  restoreAccomplishment: (id: string) => Promise<void>
  reviewAccomplishment: (id: string, action: 'confirm' | 'discard') => Promise<void>
  updateAndConfirm: (id: string, data: AccomplishmentUpdateData) => Promise<void>
  fetchVocabulary: () => Promise<void>

  // Framing actions
  fetchFramings: (accomplishmentId: string) => Promise<Framing[]>
  generateFramings: (accomplishmentId: string, labels?: string[]) => Promise<void>
  updateFraming: (accomplishmentId: string, framingId: string, text: string) => Promise<void>
  deleteFraming: (accomplishmentId: string, framingId: string) => Promise<void>
  approveFraming: (accomplishmentId: string, framingId: string) => Promise<void>
  dismissFramingReview: (accomplishmentId: string, framingId: string) => Promise<void>
  regenerateFraming: (accomplishmentId: string, framingId: string) => Promise<void>

  clearSelected: () => void
  clearError: () => void
}

export interface AccomplishmentCreateData {
  employer: string
  role_title: string
  description: string
  period?: string
  authority_notes?: string
  role_context?: string
  competency_tags?: string[]
  context_tags?: string[]
}

export interface AccomplishmentUpdateData {
  employer?: string
  role_title?: string
  period?: string
  description?: string
  authority_notes?: string
  role_context?: string
  competency_tags?: string[]
  context_tags?: string[]
}

const defaultFilters: LibraryFilters = {
  search: '',
  employer: '',
  competency: '',
  includeArchived: false,
}

export const useLibraryStore = create<LibraryState>((set, get) => ({
  accomplishments: [],
  selectedAccomplishment: null,
  filters: { ...defaultFilters },
  competencyVocabulary: [],
  contextTagVocabulary: [],
  loadState: 'idle',
  error: null,
  framingsLoading: false,

  fetchAccomplishments: async () => {
    set({ loadState: 'loading', error: null })
    try {
      const { search, employer, competency, includeArchived } = get().filters
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (employer) params.set('employer', employer)
      if (competency) params.set('competency', competency)
      if (includeArchived) params.set('include_archived', 'true')
      const query = params.toString()
      const path = `/api/v1/library${query ? `?${query}` : ''}`
      const res = await api.get<ApiResponse<Accomplishment[]>>(path)
      set({ accomplishments: res.data, loadState: 'ready' })
    } catch (err) {
      set({ error: (err as Error).message, loadState: 'error' })
    }
  },

  fetchAccomplishment: async (id: string) => {
    set({ loadState: 'loading', error: null })
    try {
      const res = await api.get<ApiResponse<Accomplishment>>(`/api/v1/library/${id}`)
      set({ selectedAccomplishment: res.data, loadState: 'ready' })
    } catch (err) {
      set({ error: (err as Error).message, loadState: 'error' })
    }
  },

  setFilter: (key: 'search' | 'employer' | 'competency', value: string) => {
    set((state) => ({ filters: { ...state.filters, [key]: value } }))
  },

  setIncludeArchived: (value: boolean) => {
    set((state) => ({ filters: { ...state.filters, includeArchived: value } }))
  },

  clearFilters: () => {
    set({ filters: { ...defaultFilters } })
  },

  createAccomplishment: async (data: AccomplishmentCreateData) => {
    set({ loadState: 'loading', error: null })
    try {
      await api.post('/api/v1/library', data)
      await get().fetchAccomplishments()
    } catch (err) {
      set({ error: (err as Error).message, loadState: 'error' })
      throw err
    }
  },

  updateAccomplishment: async (id: string, data: AccomplishmentUpdateData) => {
    set({ loadState: 'loading', error: null })
    try {
      const res = await api.put<ApiResponse<Accomplishment>>(`/api/v1/library/${id}`, data)
      set({ selectedAccomplishment: res.data, loadState: 'ready' })
      await get().fetchAccomplishments()
    } catch (err) {
      set({ error: (err as Error).message, loadState: 'error' })
      throw err
    }
  },

  archiveAccomplishment: async (id: string) => {
    set({ error: null })
    try {
      await api.delete(`/api/v1/library/${id}`)
      set({ selectedAccomplishment: null })
      await get().fetchAccomplishments()
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  restoreAccomplishment: async (id: string) => {
    set({ error: null })
    try {
      await api.post(`/api/v1/library/${id}/restore`)
      await get().fetchAccomplishments()
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  reviewAccomplishment: async (id: string, action: 'confirm' | 'discard') => {
    set({ error: null })
    try {
      await api.post(`/api/v1/library/${id}/review`, { action })
      await get().fetchAccomplishments()
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  updateAndConfirm: async (id: string, data: AccomplishmentUpdateData) => {
    set({ error: null })
    try {
      await api.put(`/api/v1/library/${id}`, data)
      await api.post(`/api/v1/library/${id}/review`, { action: 'confirm' })
      await get().fetchAccomplishments()
    } catch (err) {
      set({ error: (err as Error).message })
      throw err
    }
  },

  fetchVocabulary: async () => {
    try {
      const res = await api.get<ApiResponse<{ competencies: VocabularyTerm[]; context_tags: VocabularyTerm[] }>>(
        '/api/v1/library/vocabulary'
      )
      set({
        competencyVocabulary: res.data.competencies,
        contextTagVocabulary: res.data.context_tags,
      })
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  fetchFramings: async (accomplishmentId: string) => {
    try {
      const res = await api.get<ApiResponse<Framing[]>>(
        `/api/v1/library/${accomplishmentId}/framings`
      )
      return res.data
    } catch (err) {
      set({ error: (err as Error).message })
      return []
    }
  },

  generateFramings: async (accomplishmentId: string, labels?: string[]) => {
    set({ framingsLoading: true, error: null })
    try {
      const res = await api.post<ApiResponse<Framing[]>>(
        `/api/v1/library/${accomplishmentId}/framings`,
        { labels: labels ?? null }
      )
      // Update selected accomplishment's framings
      const selected = get().selectedAccomplishment
      if (selected && selected.id === accomplishmentId) {
        set({ selectedAccomplishment: { ...selected, framings: res.data } })
      }
      set({ framingsLoading: false })
    } catch (err) {
      set({ error: (err as Error).message, framingsLoading: false })
      throw err
    }
  },

  updateFraming: async (accomplishmentId: string, framingId: string, text: string) => {
    set({ error: null })
    try {
      await api.put(`/api/v1/library/${accomplishmentId}/framings/${framingId}`, { text })
      // Refresh selected accomplishment
      await get().fetchAccomplishment(accomplishmentId)
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  deleteFraming: async (accomplishmentId: string, framingId: string) => {
    set({ error: null })
    try {
      await api.delete(`/api/v1/library/${accomplishmentId}/framings/${framingId}`)
      await get().fetchAccomplishment(accomplishmentId)
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  approveFraming: async (accomplishmentId: string, framingId: string) => {
    set({ error: null })
    try {
      await api.post(`/api/v1/library/${accomplishmentId}/framings/${framingId}/approve`)
      await get().fetchAccomplishment(accomplishmentId)
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  dismissFramingReview: async (accomplishmentId: string, framingId: string) => {
    set({ error: null })
    try {
      await api.post(`/api/v1/library/${accomplishmentId}/framings/${framingId}/dismiss-review`)
      await get().fetchAccomplishment(accomplishmentId)
    } catch (err) {
      set({ error: (err as Error).message })
    }
  },

  regenerateFraming: async (accomplishmentId: string, framingId: string) => {
    set({ framingsLoading: true, error: null })
    try {
      await api.post(`/api/v1/library/${accomplishmentId}/framings/${framingId}/regenerate`)
      await get().fetchAccomplishment(accomplishmentId)
      set({ framingsLoading: false })
    } catch (err) {
      set({ error: (err as Error).message, framingsLoading: false })
      throw err
    }
  },

  clearSelected: () => set({ selectedAccomplishment: null }),
  clearError: () => set({ error: null }),
}))
